// SPKI pin registry (ios/BOUNDARY.md §2.2/§2.3): maps endpoint origins to
// their "sha256/<b64>" pins so the ws/http bridges can pass them to the host.
// Populated from the verified endpoint list at boot and on every accepted
// update. Pin-authoritative on the native side: when a pin is supplied the
// host's trust evaluation uses the pin instead of hostname/CA validation —
// required for kind:'ip' endpoints and resilient to a poisoned local trust
// store (Connection > everything).
//
// The registry also remembers WHICH rung an origin came from. iOS routes every
// pinned origin through its native seams anyway, but Android's brain runs in a
// WebView whose JS WebSocket/fetch can neither pin nor present a name the cert
// will match — so there the pinned-native detour is reserved for exactly the
// endpoints that cannot work without it (`kind:'ip'`), and every domain
// endpoint keeps ordinary Chromium CA + hostname validation.

import type { EndpointList } from '@merkury/protocol';

interface PinRecord {
  pins: string[];
  /** The signed list called this a raw-address endpoint AND the authority is
   *  not a nameable host — the two conditions that make CA/hostname validation
   *  structurally impossible rather than merely unwanted. */
  ip: boolean;
}

const pinsByOrigin = new Map<string, PinRecord>();

function originOf(url: string): string | null {
  // ws(s)/http(s) URLs only; origin = scheme://host[:port] lowercased.
  const m = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]+)/i.exec(url);
  const scheme = m?.[1];
  const authority = m?.[2];
  if (!scheme || !authority) return null;
  return `${scheme.toLowerCase()}://${authority.toLowerCase()}`;
}

/**
 * A host safe to treat as a NAME. Deliberately the same ALLOW-list `lib/http.ts`
 * uses to decide what may reach a log line: dot-separated labels ending in an
 * alphabetic (or `xn--`) TLD. Every spelling of a literal address fails it — a
 * dotted quad ends in digits, IPv6 carries `:`/brackets, and the decimal/hex/
 * octal forms normalize to one of those. A deny-list would have to enumerate
 * those spellings and would miss one.
 */
const LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
const DNS_NAME = new RegExp(`^${LABEL}(?:\\.${LABEL})*\\.(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$`, 'i');

/** Authority (`host[:port]`, possibly bracketed IPv6) → bare host. */
function hostOfAuthority(authority: string): string {
  const bracketed = /^\[([^\]]*)\]/.exec(authority);
  if (bracketed) return bracketed[1] ?? '';
  return authority.replace(/^[^@]*@/, '').replace(/:\d+$/, '');
}

/** True when the origin's authority is a literal address rather than a name. */
export function isAddressOrigin(origin: string): boolean {
  const authority = /^[a-z][a-z0-9+.-]*:\/\/(.+)$/i.exec(origin)?.[1];
  if (!authority) return false;
  const host = hostOfAuthority(authority);
  return host !== '' && !DNS_NAME.test(host);
}

function remember(origin: string, pin: string, ip: boolean): void {
  const existing = pinsByOrigin.get(origin) ?? { pins: [], ip: false };
  if (!existing.pins.includes(pin)) existing.pins.push(pin);
  // An origin claimed by any raw-address entry stays raw-address: the
  // authority is what decides, and it cannot be both.
  existing.ip = existing.ip || (ip && isAddressOrigin(origin));
  pinsByOrigin.set(origin, existing);
}

/** Re-seed the registry from a verified endpoint list document. */
export function setEndpointPins(list: EndpointList): void {
  pinsByOrigin.clear();
  // `diag` intakes carry the same kind/spkiPin discriminated union and are
  // reached over the same transports (protocol/endpoints.ts), so a pinned
  // intake pins exactly like a pinned endpoint.
  const all = [...(list.endpoints ?? []), ...(list.turn ?? []), ...(list.diag ?? [])];
  for (const ep of all) {
    const pin = (ep as { spkiPin?: string }).spkiPin;
    const url = (ep as { url?: string }).url;
    if (!pin || !url) continue;
    const origin = originOf(url);
    if (!origin) continue;
    const ip = (ep as { kind?: string }).kind === 'ip';
    remember(origin, pin, ip);
    // The same host serves ws and https on the same port — register the
    // sibling scheme too so pinned REST calls to a pinned ws origin match.
    const sibling = origin.startsWith('wss://')
      ? origin.replace(/^wss:/, 'https:')
      : origin.startsWith('https://')
        ? origin.replace(/^https:/, 'wss:')
        : null;
    if (sibling) remember(sibling, pin, ip);
  }
}

/** Pins registered for a URL's origin, or [] (system default trust). */
export function pinsForUrl(url: string): string[] {
  const origin = originOf(url);
  if (!origin) return [];
  return pinsByOrigin.get(origin)?.pins ?? [];
}

/**
 * Pins for a URL whose origin the signed list declared `kind:'ip'` — the last
 * rung of docs/04 §2, the one that survives every hostname being DNS/SNI
 * blocked. Empty for every domain endpoint, pinned or not, so a caller that
 * gates a pin-authoritative transport on this can never take a domain endpoint
 * off ordinary CA + hostname validation.
 */
export function pinnedIpPinsForUrl(url: string): string[] {
  const origin = originOf(url);
  if (!origin) return [];
  const record = pinsByOrigin.get(origin);
  return record?.ip ? record.pins : [];
}
